import React, { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderBy, SortDirection } from '../../../common/types';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvNoContent from '../molecules/OvNoContent';
import styled from 'styled-components';
import OvListTable from '../atoms/OvListTable';
import OvListTableRow from '../atoms/OvListTableRow';
import OvListTableHeaderField from '../atoms/OvListTableHeaderField';
import OvSurveyResponseListRow from './OvSurveyResponseListRow';
import { AdminNpsSurveyService } from '../../../services/admin/admin-nps-survey.service';
import {
  NpsSurveyResponseFields,
  NpsSurveyResponseMetaFields,
} from '../../../firebase/document-field.enums';
import {
  FilterModel,
  FilterOption,
} from '../../../common/model/ui/filter.model';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvListFilter from './OvListFilter';
import Colours from '../../../design-system/colours';
import OvNpsTrend from '../molecules/OvNpsTrend';
import OvPaginationBar from '../molecules/OvPaginationBar';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import queryString from 'query-string';
import { NPS_SURVEY_RESPONSE_LIST_FILTERS } from '../../../common/model/ui/nps-survey-response-list-filters';
import {
  getAxiosErrorMessage,
  hasAppliedListFilters,
} from '../../../common/utils/helpers';
import { FilterUtils } from '../../../common/utils/services/filter.utils';

function toFilterOptions(values: string[]): FilterOption[] {
  return values.map((value) => ({ label: value, value }));
}

const OvNpsSurveyDashboardTab: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [surveyResponseListSorting, setSurveyResponseListSorting] =
    useState<OrderBy | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});

  const { data: appVersions = [] } = useQuery({
    queryKey: ['admin-nps-survey-app-versions'],
    queryFn: async () =>
      (await AdminNpsSurveyService.loadAllAppVersions()).map(
        ({ app_version }) => app_version
      ),
  });
  const { data: devices = [] } = useQuery({
    queryKey: ['admin-nps-survey-devices'],
    queryFn: async () =>
      (await AdminNpsSurveyService.loadAllDevices()).map(
        ({ device_model }) => device_model
      ),
  });
  const { data: osVersions = [] } = useQuery({
    queryKey: ['admin-nps-survey-os-versions'],
    queryFn: async () =>
      (await AdminNpsSurveyService.loadAllOsVersions()).map(
        ({ os_version }) => os_version
      ),
  });
  const { data: netPromoterScore, error: netPromoterScoreError } = useQuery({
    queryKey: ['admin-nps-survey-net-promoter-score'],
    queryFn: () => AdminNpsSurveyService.getNetPromoterScore(),
  });

  const filterOptions: Record<string, FilterOption[]> = {
    [NpsSurveyResponseMetaFields.app_version]: toFilterOptions(appVersions),
    [NpsSurveyResponseMetaFields.device_model]: toFilterOptions(devices),
    [NpsSurveyResponseMetaFields.os]: toFilterOptions(osVersions),
  };
  const surveyResponseListFilters = NPS_SURVEY_RESPONSE_LIST_FILTERS.map(
    (filter) => ({
      ...filter,
      value: filterValues[filter.fieldName] ?? filter.value,
      options: filterOptions[filter.fieldName] ?? filter.options,
    })
  );

  const query = queryString.stringify(
    {
      offset,
      limit: AdminNpsSurveyService.RESPONSE_LIST_LIMIT,
      order_by: surveyResponseListSorting?.order_by ?? null,
      order_dir: surveyResponseListSorting?.order_dir ?? null,
      ...FilterUtils.getQueryParams(
        FilterUtils.mapFilterModelsToQueryParams(surveyResponseListFilters)
      ),
    },
    { skipNull: true }
  );
  const {
    data: paginatedSurveyResponses,
    isFetching: isSurveyResponseListFetching,
    error: surveyResponseListError,
  } = useQuery({
    queryKey: ['admin-nps-survey-responses', query],
    queryFn: () => AdminNpsSurveyService.getSurveyResponses(query),
  });
  const surveyResponseList = paginatedSurveyResponses?.docs ?? [];

  const deleteResponse = useMutation({
    mutationFn: (responseId: string) =>
      AdminNpsSurveyService.deleteSurveyResponse(responseId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['admin-nps-survey-responses'],
      }),
  });

  const isLoading = isSurveyResponseListFetching || deleteResponse.isPending;
  const error =
    surveyResponseListError ?? deleteResponse.error ?? netPromoterScoreError;

  let surveyResponseListTable: ReactNode | null = null;

  const goToPage = (event: any, value: number) => {
    setOffset((value - 1) * AdminNpsSurveyService.RESPONSE_LIST_LIMIT);
  };

  const handleDeleteResponse = (responseId: string) => {
    deleteResponse.mutate(responseId);
  };

  const onSortByColumn = (orderBy: OrderBy) => {
    setSurveyResponseListSorting(orderBy);
    setOffset(0);
  };

  const getCurrentSortDirection = (
    columnName: NpsSurveyResponseFields
  ): SortDirection =>
    columnName === surveyResponseListSorting?.order_by
      ? surveyResponseListSorting?.order_dir
      : 'none';

  const onFilterChange = ({ fieldName, value }: FilterModel) => {
    setFilterValues((values) => ({
      ...values,
      [fieldName]: FilterUtils.getParsedFilterValue(value),
    }));
    setOffset(0);
  };

  const onFilterClear = () => {
    setFilterValues({});
    setOffset(0);
  };

  if (surveyResponseList.length) {
    surveyResponseListTable = (
      <OvListTableWrapper>
        <OvListTable>
          <OvListTableRow>
            <OvListTableHeaderField isSortable={false}>
              {t('surveyResponseList.tableFields.userName')}
            </OvListTableHeaderField>
            <OvListTableHeaderField isSortable={false}>
              {t('surveyResponseList.tableFields.npsScore')}
            </OvListTableHeaderField>
            <OvListTableHeaderField isSortable={false}>
              {t('surveyResponseList.tableFields.group')}
            </OvListTableHeaderField>
            <OvListTableHeaderField
              sortDirection={getCurrentSortDirection(
                NpsSurveyResponseFields.submitted_at
              )}
              isActiveSort={
                surveyResponseListSorting?.order_by ===
                NpsSurveyResponseFields.submitted_at
              }
              sortBy={NpsSurveyResponseFields.submitted_at}
              onSort={onSortByColumn}
            >
              {t('surveyResponseList.tableFields.submittedAt')}
            </OvListTableHeaderField>
            <OvListTableHeaderField isSortable={false}>
              {t('surveyResponseList.tableFields.responses')}
            </OvListTableHeaderField>
            <OvListTableHeaderField isSortable={false}>
              {t('common.operation')}
            </OvListTableHeaderField>
          </OvListTableRow>

          {surveyResponseList.map((response) => (
            <OvListTableRow key={response.id}>
              <OvSurveyResponseListRow
                surveyResponse={response}
                deleteResponseCallback={handleDeleteResponse}
              />
            </OvListTableRow>
          ))}
        </OvListTable>
      </OvListTableWrapper>
    );
  }

  return (
    <>
      {netPromoterScore && (
        <TrendWrapper>
          <OvNpsTrend netPromoterScore={netPromoterScore} />
        </TrendWrapper>
      )}

      <Container>
        <CenterPane>
          <PaginationWrapper>
            <OvPaginationBar
              total={paginatedSurveyResponses?.total ?? 0}
              limit={AdminNpsSurveyService.RESPONSE_LIST_LIMIT}
              offset={offset}
              onChange={goToPage}
            />
          </PaginationWrapper>
          {error && (
            <ErrorMessageWrapper>
              <OvErrorMessage message={getAxiosErrorMessage(error)} />
            </ErrorMessageWrapper>
          )}
          {surveyResponseListTable}
          {!surveyResponseList.length && !isLoading ? (
            <OvNoContent>{t('surveyResponseList.empty')}</OvNoContent>
          ) : null}
          {isLoading && <OvLoadingIndicator position="fixed" />}
        </CenterPane>

        <FilterListWrapper>
          <OvListFilter
            filters={surveyResponseListFilters}
            isOpened={true}
            hasAppliedFilters={hasAppliedListFilters(surveyResponseListFilters)}
            onRemoveFilters={onFilterClear}
            onHandleFilterValueChange={onFilterChange}
          />
        </FilterListWrapper>
      </Container>
    </>
  );
};

export default OvNpsSurveyDashboardTab;

const Container = styled.section`
  display: flex;
`;

const TrendWrapper = styled.div`
  margin-bottom: 1rem;
`;

const CenterPane = styled.div`
  padding-right: 1rem;
  border-right: 1px solid ${Colours.OV_LIGHT_GRAY};
  flex-grow: 1;
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const OvListTableWrapper = styled.div`
  overflow: auto hidden;
  -ms-overflow-style: none; // Internet Explorer 10+
  scrollbar-width: none; // Firefox
  &::-webkit-scrollbar {
    display: none; // Safari and Chrome
  }
`;

const ErrorMessageWrapper = styled.div`
  margin: 1rem 0;
`;

const FilterListWrapper = styled.div`
  margin-right: -1.375rem;
`;
